import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Loader2, Camera, Image, X, Sparkles, CheckCircle2 } from "lucide-react";
import imageCompression from 'browser-image-compression';
import { CameraCapture } from "@/components/camera/CameraCapture";
import { CaptureResult } from "@/types/camera";
import { useNativeHaptics } from "@/hooks/useNativeHaptics";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";

interface CreateStoryPremiumProps {
  onClose: () => void;
}

export const CreateStoryPremium = ({ onClose }: CreateStoryPremiumProps) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const haptics = useNativeHaptics();

  const createStoryMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Nenhum arquivo selecionado');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let fileToUpload = file;

      // Compress image if needed
      if (file.type.startsWith('image/')) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: 'image/jpeg'
        };
        fileToUpload = await imageCompression(file, options);
      }

      // Upload do arquivo
      const fileExt = fileToUpload.type.startsWith('video/') ? 'mp4' : 'jpg';
      const fileName = `${user.id}/story-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('stories')
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('stories')
        .getPublicUrl(fileName);

      // Criar registro do story
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
      
      const { error: insertError } = await supabase
        .from('stories')
        .insert({
          user_id: user.id,
          media_type: mediaType,
          media_url: publicUrl,
          caption: caption || null,
          duration_seconds: mediaType === 'video' ? 15 : 5
        });

      if (insertError) throw insertError;
    },
    onSuccess: () => {
      haptics.success();
      setIsSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['stories-carousel'] });
      queryClient.invalidateQueries({ queryKey: ['user-stories'] });
      
      setTimeout(() => {
        onClose();
      }, 1500);
    },
    onError: (error: any) => {
      haptics.error();
      toast.error(error.message || "Erro ao publicar story");
    }
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validar tipo
    if (!selectedFile.type.startsWith('image/') && !selectedFile.type.startsWith('video/')) {
      haptics.warning();
      toast.error('Apenas imagens e vídeos são permitidos');
      return;
    }

    // Validar tamanho (10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      haptics.warning();
      toast.error('Arquivo muito grande. Máximo 10MB');
      return;
    }

    haptics.light();
    setFile(selectedFile);
    
    // Preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleCameraCapture = (result: CaptureResult) => {
    setCameraOpen(false);
    haptics.medium();
    
    // Converter blob para File
    const capturedFile = new File(
      [result.blob], 
      `story-${Date.now()}.${result.type === 'photo' ? 'jpg' : 'webm'}`,
      { type: result.blob.type }
    );
    
    setFile(capturedFile);
    setPreview(result.dataUrl);
  };

  const handleSubmit = () => {
    if (!file) {
      haptics.warning();
      toast.error('Selecione uma foto ou vídeo');
      return;
    }
    haptics.medium();
    createStoryMutation.mutate();
  };

  const clearSelection = () => {
    haptics.light();
    setFile(null);
    setPreview(null);
  };

  if (cameraOpen) {
    return (
      <CameraCapture
        onCapture={handleCameraCapture}
        onClose={() => setCameraOpen(false)}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Premium Header */}
      <DrawerHeader className="border-b border-border/30 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <DrawerTitle className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Criar Story
          </DrawerTitle>
          <DrawerClose asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
              <X className="h-5 w-5" />
            </Button>
          </DrawerClose>
        </div>
      </DrawerHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Success Animation */}
        <AnimatePresence>
          {isSuccess && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.1 }}
                className="flex flex-col items-center gap-4"
              >
                <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-12 h-12 text-green-500" />
                </div>
                <p className="text-lg font-semibold">Story publicado!</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Preview Area */}
        {preview ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative aspect-[9/16] rounded-2xl overflow-hidden bg-muted shadow-xl"
          >
            {file?.type.startsWith('video/') ? (
              <video src={preview} className="w-full h-full object-cover" controls />
            ) : (
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            )}
            
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none" />
            
            {/* Change button */}
            <Button
              variant="secondary"
              size="sm"
              className="absolute top-3 right-3 backdrop-blur-xl bg-background/80 hover:bg-background/90 shadow-lg"
              onClick={clearSelection}
            >
              <X className="w-4 h-4 mr-1.5" />
              Trocar
            </Button>
          </motion.div>
        ) : (
          /* Action Buttons */
          <div className="space-y-3">
            {/* Camera Button - Primary */}
            <motion.div
              whileTap={{ scale: 0.98 }}
            >
              <Button
                variant="default"
                className="w-full h-28 rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-primary/80 hover:from-primary/95 hover:to-primary/75 transition-all shadow-lg shadow-primary/25"
                onClick={() => {
                  haptics.light();
                  setCameraOpen(true);
                }}
              >
                <div className="flex flex-col items-center gap-2">
                  <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
                    <Camera className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-white">Abrir Câmera</span>
                </div>
              </Button>
            </motion.div>
            
            {/* Gallery Button - Secondary */}
            <motion.div
              whileTap={{ scale: 0.98 }}
            >
              <Button
                variant="outline"
                className="w-full h-20 border-2 border-dashed border-border/60 hover:border-primary/50 rounded-2xl bg-muted/30 hover:bg-muted/50 transition-all"
                onClick={() => {
                  haptics.light();
                  fileInputRef.current?.click();
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-full bg-muted">
                    <Image className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium block">Escolher da Galeria</span>
                    <p className="text-[10px] text-muted-foreground">Fotos e vídeos até 10MB</p>
                  </div>
                </div>
              </Button>
            </motion.div>
          </div>
        )}

        {/* Caption Input - Only show when file selected */}
        {preview && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Adicione uma legenda..."
              maxLength={200}
              className="h-12 rounded-xl bg-muted/50 border-border/50 focus:border-primary/50"
            />
            <p className="text-[10px] text-muted-foreground mt-1.5 text-right">
              {caption.length}/200
            </p>
          </motion.div>
        )}
      </div>

      {/* Fixed Bottom Action */}
      {preview && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-shrink-0 p-4 border-t border-border/30 bg-background"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 16px) + 16px)' }}
        >
          <Button
            onClick={handleSubmit}
            disabled={!file || createStoryMutation.isPending}
            className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/25 transition-all"
          >
            {createStoryMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Publicando...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Publicar Story
              </>
            )}
          </Button>
        </motion.div>
      )}
    </div>
  );
};
